import { useEffect, useState } from "react";
import { ArrowUp, ArrowRight, CheckCircle, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import { SEO } from "@/components/SEO";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import { ProductGallery } from "@/components/ui/product-gallery";
import { Footer } from "@/components/Footer";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { LocalizedLink } from "@/components/LocalizedLink";
import { PriceFrom } from "@/components/products/PriceFrom";
import { useI18n } from "@/i18n";
import { collection2026, c26Lang } from "@/data/collection2026";

const ui = {
  en: { badge: "REM7 Collection 2027 · Arriving soon", back: "Back to Collection 2027", gallery: "Product Gallery", features: "Key Features",
    ctaTitle: (n: string) => `Interested in the ${n}?`, ctaText: "Talk to our team about availability, project quantities and delivery planning in Spain.",
    contact: "Contact Sales Team" },
  es: { badge: "Colección REM7 2027 · Próximamente", back: "Volver a la Colección 2027", gallery: "Galería del producto", features: "Características principales",
    ctaTitle: (n: string) => `¿Le interesa el ${n}?`, ctaText: "Hable con nuestro equipo sobre disponibilidad, cantidades para proyectos y planificación de entregas en España.",
    contact: "Contactar con ventas" },
};

function Collection2026Product({ slug }: { slug: string }) {
  const { language } = useI18n();
  const lang = c26Lang(language);
  const p = collection2026.find((x) => x.slug === slug)!;
  const c = p[lang];
  const u = ui[lang];
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();
  useEffect(() => {
    const h = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const url = `/collection-2027/${p.slug}`;
  const galleryImages = p.images.map((src, i) => ({ src, alt: `${p.name} — ${c.kicker} (${i + 1})`, title: p.name }));
  const structuredData = {
    "@context": "https://schema.org", "@type": "Product", name: p.name, description: c.copy,
    category: "Smart Toilets", url: `https://www.aureum7trading.com/${language}${url}`,
    image: p.images.map((s) => `https://www.aureum7trading.com${s}`),
    brand: { "@type": "Brand", name: "Aureum VII Trading" },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted text-foreground">
      <SEO title={`${p.name} | ${c.tagline}`} description={c.copy.slice(0, 155)} structuredData={structuredData} url={url} image={`https://www.aureum7trading.com${p.images[0]}`} />
      <Navigation />

      <section className="pt-24 pb-12 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-observer">
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-medium">{u.badge}</Badge>
              <h1 className="font-playfair text-5xl md:text-6xl font-light mb-6 text-primary">
                {p.name}
                <span className="block text-2xl md:text-3xl text-muted-foreground font-medium mt-2">{c.tagline}</span>
              </h1>
              <p className="font-inter text-sm uppercase tracking-wider text-accent mb-4">{c.kicker}</p>
              <p className="font-inter text-xl text-muted-foreground mb-8 leading-relaxed">{c.copy}</p>
              <PriceFrom price={600} />
              <div className="mb-8 space-y-3">
                {c.features.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-accent rounded-full" />
                    <span className="font-inter text-muted-foreground">{f}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild><LocalizedLink to="/contact?intent=trade#enquiry-form">{u.contact}</LocalizedLink></Button>
                <Button variant="outline" asChild><LocalizedLink to="/collection-2027">{u.back}</LocalizedLink></Button>
              </div>
            </div>
            <div className="fade-in-observer">
              <div className="relative">
                <img src={p.images[0]} alt={`${p.name} — ${c.tagline}`} className="w-full h-auto rounded-2xl shadow-luxury" loading="eager" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">{u.gallery}</h2>
          <ProductGallery images={galleryImages.slice(1)} className="max-w-4xl mx-auto" />
        </div>
      </section>

      <section className="py-16 px-6 lg:px-12 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">{u.features}</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {c.features.map((f) => (
              <div key={f} className="bg-white/80 backdrop-blur-sm shadow-soft rounded-lg p-6 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                <p className="font-inter text-muted-foreground">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer"><ShippingTimeInfo variant="detailed" /></div>
      </section>

      <section className="py-16 px-6 lg:px-12 bg-gradient-to-r from-primary via-primary to-accent text-primary-foreground">
        <div className="container mx-auto max-w-4xl text-center fade-in-observer">
          <h2 className="font-playfair text-4xl font-light mb-6">{u.ctaTitle(p.name)}</h2>
          <p className="font-inter text-lg opacity-90 mb-8 leading-relaxed">{u.ctaText}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-background text-foreground hover:bg-background/90 font-inter font-semibold px-8 py-3 shadow-lg">
              <LocalizedLink to="/contact?intent=trade#enquiry-form">{u.contact}<ArrowRight className="ml-2 w-4 h-4" /></LocalizedLink>
            </Button>
            <Button asChild variant="outline" className="border-primary-foreground bg-transparent text-primary-foreground hover:bg-primary-foreground/10 font-inter px-8 py-3">
              <a href="tel:+34688495309"><Phone className="w-4 h-4 mr-2" />+34 688 495 309</a>
            </Button>
            <Button asChild variant="outline" className="border-primary-foreground bg-transparent text-primary-foreground hover:bg-primary-foreground/10 font-inter px-8 py-3">
              <a href="mailto:info@aureum7trading.com"><Mail className="w-4 h-4 mr-2" />info@aureum7trading.com</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      {showBackToTop && (
        <Button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top"
          className="fixed bottom-8 right-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-3 shadow-luxury z-50">
          <ArrowUp className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
}

export const Rem7Sculpture132 = () => <Collection2026Product slug="132-the-sculpture" />;
export const Rem7Midnight153 = () => <Collection2026Product slug="153-midnight-edition" />;
export const Rem7Suspended158A = () => <Collection2026Product slug="158a-suspended" />;
export const Rem7MonolithM86 = () => <Collection2026Product slug="m86-monolith" />;
